import {useState, useEffect} from 'react';
import {
  DataGrid,
  GridColumns,
  GridFeatureMode,
  GridSlotsComponent,
  GridRowParams,
  GridSelectionModel,
} from '@mui/x-data-grid';
import {gridStyles, useStyles} from './styles';
import {IPlanAndMeetStatus} from '@app/screens/module-specific-utilities/pages/plan-and-meet/redux/slice';

interface DataTableGridData {
  columns: GridColumns;
  data: IPlanAndMeetStatus[];
  pageSizeOptions: number[];
  onPageChange: (params: any) => any;
  onPageSizeChanged: (params: any) => any;
  onSortingChange: (params: any) => any;
  rowCount: number;
  pgSize: number;
  paginationMode: GridFeatureMode;
  page: number;
  components?: Partial<GridSlotsComponent>;
  checkBoxRequired?: boolean;
  handleSelectedRow?: (params: any, ids: Array<string>) => any;
  checkRowsSelectable?: number;
  keepNonExistentRowsSelected?: boolean;
  hideGrid?: boolean;
  allSelected?: boolean;
  selectedRows?: Array<any>;
  deselectedRows?: Array<any>;
  uniqueKey?: string;
}

export const DataTableGrid: React.FunctionComponent<DataTableGridData> = ({
  columns,
  data,
  pageSizeOptions,
  onPageChange,
  onPageSizeChanged,
  onSortingChange,
  rowCount,
  pgSize = 10,
  paginationMode = 'client',
  page = 0,
  components,
  checkBoxRequired = false,
  handleSelectedRow = () => {},
  checkRowsSelectable = 2,
  keepNonExistentRowsSelected = false,
  hideGrid = false,
  allSelected = false,
  selectedRows = [],
  deselectedRows = [],
  uniqueKey,
}) => {
  const classes = useStyles();
  const [pageSize, setPageSize] = useState(pgSize);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);

  useEffect(() => {
    if (allSelected) {
      let newRows: Array<any> = [];
      data?.forEach(item => {
        const exists =
          selectionModel.includes(item.id) ||
          deselectedRows.find((row: any) => row.id === item.id);
        if (!exists) {
          newRows.push(item.id);
        }
      });
      setSelectionModel([...selectionModel, ...newRows]);
    }
  }, [data]);

  useEffect(() => {
    if (
      (!checkBoxRequired || !selectedRows?.length) &&
      selectionModel?.length
    ) {
      setSelectionModel([]);
    }
  }, [checkBoxRequired, selectedRows, selectionModel]);

  const pageSizeChanged = (value: any) => {
    onPageSizeChanged(value);
    setPageSize(value);
  };

  const getRowIdForGrid = (obj: any) => {
    if (uniqueKey) {
      return obj[uniqueKey];
    } else if (obj.hasOwnProperty('id')) {
      return obj?.id;
    } else if (obj.hasOwnProperty('staffPositionId')) {
      return obj?.staffPositionId;
    } else if (obj.hasOwnProperty('divisionId')) {
      return obj?.divisionId;
    } else if (obj.hasOwnProperty('partyId')) {
      return obj?.partyId;
    }
  };

  return (
    <DataGrid
      columnBuffer={8}
      sx={{
        '& .MuiDataGrid-row:hover': {},
        ...gridStyles,
      }}
      page={page}
      className={classes.grid}
      style={{visibility: hideGrid ? 'hidden' : 'visible'}}
      rows={data}
      getRowId={getRowIdForGrid}
      columns={columns}
      pageSize={pageSize}
      onPageSizeChange={newPageSize => pageSizeChanged(newPageSize)}
      rowsPerPageOptions={pageSizeOptions}
      onPageChange={onPageChange}
      onSortModelChange={onSortingChange}
      pagination
      rowCount={rowCount}
      paginationMode={paginationMode}
      disableSelectionOnClick
      components={components}
      checkboxSelection={checkBoxRequired}
      keepNonExistentRowsSelected={keepNonExistentRowsSelected}
      selectionModel={selectionModel}
      onSelectionModelChange={(ids: any, details: any) => {
        setSelectionModel(ids);
        const selectedIDs = new Set(ids);
        const selectedRowData = data?.filter(row =>
          selectedIDs.has(row?.id?.toString()),
        );
        handleSelectedRow(selectedRowData, ids);
      }}
      isRowSelectable={(params: GridRowParams) =>
        params.row.unlockType === checkRowsSelectable
      }
    />
  );
};
